/**
 * 학생 v2 화면들이 함께 쓰는 작은 부품 — 상태 배지 · 탭 단추 · 상자 · 숫자 카드 · 안내 · 불러오기 훅.
 * 얼굴은 학습 v2(StudentsHub)에서 옮겨 온다 — 설계 §9-2 대조표.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Loader2, MoreHorizontal, RotateCcw } from 'lucide-react';
import { ActionButton, actionIcon } from './controls';
import { isPredeployError, describeAcademyError, PREDEPLOY_TEXT } from './academyApi';
import { stateBadge } from './academyModel';
import { lockedLabel, lockedStage } from './stageGate';

/** 배지 색 — 설계 §2-1 (초록 수강 중 · 노랑 휴원 · 빨강 퇴원 예정 · 파랑 등록 예정 · 회색 퇴원) */
const TONE_CLASS = {
  ok: 'bg-primary/15 text-primary-dark',
  warn: 'bg-yellow-100 text-yellow-800',
  danger: 'bg-red-50 text-danger',
  info: 'bg-blue-50 text-blue-700',
  muted: 'bg-gray-100 text-gray-500',
};

export function ToneBadge({ tone = 'muted', children, title }) {
  return (
    <span title={title} className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONE_CLASS[tone] || TONE_CLASS.muted}`}>
      {children}
    </span>
  );
}

/**
 * 상태 배지. twoLine — 표의 96px 칸에서는 「퇴원 예정 11/1」 한 줄이 넘쳐 배지와 날짜를 두 줄로(설계 §1 모형 실측).
 */
export function StateBadge({ row, twoLine = false }) {
  const b = stateBadge(row);
  if (b.date && twoLine) {
    return (
      <span className="inline-flex flex-col items-end gap-0.5">
        <ToneBadge tone={b.tone}>{b.label}</ToneBadge>
        <span className="text-xs leading-tight text-gray-500">{b.date}</span>
      </span>
    );
  }
  return <ToneBadge tone={b.tone}>{b.date ? `${b.label} ${b.date}` : b.label}</ToneBadge>;
}

/** 보기 전환 단추 — 학습 v2 TableViewToggle(StudentLearningPanel.jsx:32-46)과 같은 얼굴 */
export function SegTabs({ tabs, value, onChange, ariaLabel, compact = false }) {
  return (
    <div className="flex min-w-0 max-w-full gap-0.5 overflow-x-auto rounded-3xl bg-gray-100 p-0.5 [scrollbar-width:none]" role="group" aria-label={ariaLabel}>
      {tabs.map(([k, label]) => (
        <button
          key={k}
          type="button"
          aria-pressed={value === k}
          onClick={() => onChange(k)}
          className={`min-h-8 shrink-0 whitespace-nowrap rounded-3xl py-1 text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${compact ? 'min-w-6 px-1.5' : 'px-3'} ${value === k ? 'bg-primary font-medium text-white' : 'text-gray-600 hover:bg-gray-200'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** 상자 — 통계·상세 본문의 한 덩어리 */
export function Box({ title, sub, right, children, className = '', compact = false }) {
  return (
    <section className={`min-w-0 rounded-2xl border border-gray-200 bg-white ${compact ? 'p-2' : 'p-3'} ${className}`}>
      {(title || right) && (
        <div className={`flex min-w-0 flex-wrap items-center ${compact ? 'mb-1.5 gap-x-2 gap-y-1' : 'mb-3 gap-2'}`}>
          {title && <h4 className="shrink-0 text-sm font-semibold text-gray-900">{title}</h4>}
          {sub && <span className="min-w-0 flex-1 text-xs text-gray-500">{sub}</span>}
          {right && <span className="ml-auto shrink-0">{right}</span>}
        </div>
      )}
      {children}
    </section>
  );
}

/** 숫자 카드 — 통계 머리의 숫자 띠. loading 이면 회색 자리 표시(설계 §8) */
export function KpiCard({ label, value, sub, warn = false, loading = false, onClick, compact = false, displayLabel = label }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag {...(onClick ? { type: 'button', onClick, 'aria-label': `${label} 자세히` } : {})} className={`min-w-0 rounded-2xl border border-gray-200 text-left ${compact ? 'px-2 py-1.5' : 'px-2.5 py-2'} ${onClick ? 'transition-colors hover:border-primary hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary' : ''}`}>
      <p title={label} className="truncate text-xs text-gray-500">{displayLabel}</p>
      {loading ? (
        <div className="mt-1 h-5 w-10 animate-pulse rounded bg-gray-100" />
      ) : (
        <p className={`mt-0.5 text-lg font-bold leading-tight ${warn ? 'text-danger' : 'text-gray-900'}`}>{value ?? '—'}</p>
      )}
      {(!compact || sub || loading) && <p title={sub} className="truncate text-xs text-gray-500">{loading ? ' ' : sub || ' '}</p>}
    </Tag>
  );
}

/** 불러오는 중 — 학습 v2 목록과 같은 초록 회전 아이콘 24px(설계 §8) */
export function Spinner({ label = '불러오는 중', compact = false }) {
  return (
    <div className={`flex justify-center ${compact ? 'py-3' : 'py-12'}`}>
      <span role="status" aria-label={label} className="inline-flex">
        <Loader2 aria-hidden="true" className="size-6 animate-spin text-primary" />
      </span>
    </div>
  );
}

/**
 * 오류·배포 전 안내. 배포 전 서버(봉투 없는 404·405)는 오류로 보이지 않게 회색 글(설계 §8),
 * 나머지 오류는 붉은 글 한 줄 + [다시 불러오기].
 */
export function ErrorNotice({ error, onRetry, retryLabel = '다시 불러오기', compact = false }) {
  if (!error) return null;
  if (isPredeployError(error)) {
    return (
      <div role="status" className={`text-center text-sm text-gray-500 ${compact ? 'py-3' : 'py-12'}`}>
        {PREDEPLOY_TEXT}
      </div>
    );
  }
  return (
    <div role="alert" className={`flex items-center justify-center gap-2 px-3 text-xs text-danger ${compact ? 'py-3' : 'py-8'}`}>
      <span>{describeAcademyError(error)}</span>
      {onRetry && <ActionButton icon={RotateCcw} onClick={onRetry}>{retryLabel}</ActionButton>}
    </div>
  );
}

/**
 * 아직 서버에 없는 기능 자리 — 회색 안내 한 줄(stageGate). 서버를 부르지 않는다.
 * 그 단계가 열리면 이 자리에 원래 상자가 선다.
 */
export function LockedNotice({ feature, compact = false }) {
  const stage = lockedStage(feature);
  if (!stage) return null;
  return (
    <div role="status" className={`text-center text-xs text-gray-500 ${compact ? 'py-3' : 'py-8'}`}>
      준비 중입니다.
    </div>
  );
}

/** 학생 이름 링크 — 누르면 그 학생 상세(설계 §3 「파란 이름」) */
export function NameLink({ id, name, onOpen }) {
  if (!onOpen || id == null) return <span>{name}</span>;
  return (
    <button type="button" onClick={() => onOpen(id)} className="text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
      {name}
    </button>
  );
}

/**
 * 비동기 불러오기 — key 가 바뀌면 다시 부르고, 앞 요청은 취소한다. reload() 로 같은 key 를 다시.
 * loader 는 signal 을 받는다. 취소된 요청의 결과는 버린다.
 */
export function useAsync(loader, key) {
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const [nonce, setNonce] = useState(0);
  const [state, setState] = useState({ data: null, error: null, loading: true });
  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    setState((s) => ({ data: s.data, error: null, loading: true }));
    Promise.resolve()
      .then(() => loaderRef.current(controller.signal))
      .then(
        (data) => { if (alive) setState({ data, error: null, loading: false }); },
        (error) => { if (alive && error?.name !== 'AbortError') setState({ data: null, error, loading: false }); },
      );
    return () => { alive = false; controller.abort(); };
  }, [key, nonce]);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}

/** 요소의 폭 — 통계·상세는 오른쪽 칸 폭(612 · 721 · 1,172)에 맞춰 단을 바꾼다(설계 §9-1) */
export function useElementWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

// ── 저장 뒤 새로 고침 — 쓰기가 끝나면 알리고, 목록·상세·통계가 함께 다시 부른다 ──

const CHANGED_EVT = 'student-admin-v2:changed';
let changedVersion = 0;
/** 쓰기가 끝났음을 알린다 — 창(모달)이 저장에 성공하면 부른다 */
export function notifyAcademyChanged() {
  changedVersion += 1;
  window.dispatchEvent(new Event(CHANGED_EVT));
}
/** 바뀜 번호 — useAsync 열쇠에 섞으면 저장 뒤 다시 부른다 */
export function useAcademyVersion() {
  const [v, setV] = useState(changedVersion);
  useEffect(() => {
    const on = () => setV(changedVersion);
    window.addEventListener(CHANGED_EVT, on);
    return () => window.removeEventListener(CHANGED_EVT, on);
  }, []);
  return v;
}

/** 펼침 메뉴의 층 — 강의 v2 떠 있는 판(FloatingPanel FLOATING_Z)과 같은 단, 창(Modal 9999980)보다 아래 */
const MENU_Z = 9999975;

/**
 * 펼침 메뉴 — 단추를 누르면 아래로 항목이 열리고, 항목을 누르거나 바깥을 누르거나 스크롤하면 닫힌다.
 * items: [{ key, label, onSelect, danger?, divider?, lockedAt? }]
 *   lockedAt — 아직 서버에 없는 작업(stageGate)의 단계. 잠긴 줄로 보이고 누를 수 없다(「5단계에 열림」)
 *
 * 메뉴는 body 로 띄운다(포털, position: fixed). 칸 안에 두면 전역 preserve-3d(App.css) 때문에 뒤의 본문 칸에 가려지고,
 * 스크롤 칸 안에서는 잘린다(2026-10-10 오너 「미니 모달이 뒤로 가려져있음」).
 */
export function MenuButton({ label, items, className = '', align = 'left', prominent = false }) {
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const open = pos != null;
  const iconOnly = label === '⋯';
  const plainLabel = label.replace(/\s*▾$/, '');
  const toggle = () => {
    if (open) { setPos(null); return; }
    const r = btnRef.current.getBoundingClientRect();
    setPos(align === 'right' ? { top: r.bottom + 4, right: window.innerWidth - r.right } : { top: r.bottom + 4, left: r.left });
  };
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setPos(null);
    };
    const shut = (e) => {
      if (e?.type === 'scroll' && e.target === menuRef.current) return;
      setPos(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') { setPos(null); btnRef.current?.focus(); } };
    document.addEventListener('mousedown', close);
    window.addEventListener('resize', shut);
    window.addEventListener('scroll', shut, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('resize', shut);
      window.removeEventListener('scroll', shut, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  useLayoutEffect(() => {
    if (!open || !menuRef.current) return;
    const menu = menuRef.current.getBoundingClientRect();
    const trigger = btnRef.current.getBoundingClientRect();
    setPos((p) => ({
      ...p,
      top: menu.bottom > window.innerHeight - 8 ? Math.max(8, trigger.top - menu.height - 4) : p.top,
      ...(align === 'right' ? { right: Math.max(8, window.innerWidth - trigger.right) }
        : { left: Math.max(8, Math.min(trigger.left, window.innerWidth - menu.width - 8)) }),
    }));
  }, [open, align]);
  return (
    <>
      <ActionButton ref={btnRef} icon={iconOnly ? MoreHorizontal : ChevronDown}
        prominent={prominent}
        aria-label={iconOnly ? '반 편성 작업' : plainLabel} title={iconOnly ? '반 편성 작업' : undefined}
        aria-haspopup="menu" aria-expanded={open} onClick={toggle}
        className={className}>
        {iconOnly ? null : plainLabel}
      </ActionButton>
      {open && createPortal(
        <div ref={menuRef} role="menu" style={{ position: 'fixed', zIndex: MENU_Z, ...pos }} className="max-h-[calc(100dvh-1rem)] min-w-44 max-w-[calc(100vw-1rem)] overflow-y-auto rounded-xl border border-gray-200 bg-white p-1 shadow-lg">
          {items.map((it) => {
            if (it.divider) return <div key={it.key} className="my-1 border-t border-gray-100" />;
            const Glyph = actionIcon(it.label);
            const glyph = Glyph ? <Glyph aria-hidden="true" className="size-3.5 shrink-0" /> : <span aria-hidden="true" className="size-3.5 shrink-0" />;
            if (it.lockedAt) {
              return (
                <button key={it.key} type="button" role="menuitem" disabled aria-disabled="true" className="flex min-h-8 w-full cursor-not-allowed items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-gray-400">
                  {glyph}
                  {it.label}
                  <span className="ml-auto text-[11px]">{lockedLabel(it.lockedAt)}</span>
                </button>
              );
            }
            return (
              <button
                key={it.key}
                type="button"
                role="menuitem"
                onClick={() => { setPos(null); it.onSelect(); }}
                className={`flex min-h-8 w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${it.danger ? 'text-danger hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                {glyph}{it.label}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}
